import { createChart } from '@/analysis/helpers'
import { 
  mergeChartOptions, 
  chartTooltip, 
  percentageTooltipCallback 
} from '@/analysis/utils/chart-formatters'
import { calculateAllPnlMetrics } from '@/analysis/utils/pnl'
import { formatDecimalBasedPercentage, shortenCurrency, formatCurrency } from '@/utils/string'

/**
 * Generates a comparison chart for a specific P&L sub-category (e.g., Wages, Rent, Ads).
 * Supports looking up the value in multiple main categories (e.g., OPEX vs Non-OPEX).
 */
export function generatePremiumSubCategoryComparisonChart(
  reportA: any,
  reportB: any,
  config: {
    canvasId: string
    mainCategory: string | string[] // [FIX] Support Array
    subCategory: string
    title: string
    alsoStore?: any
  }
) {
  // 1. Helper to safely sum values from potentially multiple categories
  const getSubCategoryValue = (report: any) => {
    if (!report || !report.pnlData) return 0;

    let total = 0;
    const categories = Array.isArray(config.mainCategory) ? config.mainCategory : [config.mainCategory];

    categories.forEach((cat) => {
      if (report.pnlData[cat] && report.pnlData[cat][config.subCategory]) {
        total += report.pnlData[cat][config.subCategory];
      }
    });
    return total;
  };

  // 2. Calculate Values & Percentages
  const valueA = getSubCategoryValue(reportA);
  const valueB = getSubCategoryValue(reportB);

  const getRevenue = (r: any) => calculateAllPnlMetrics(r?.pnlData || {})['Pendapatan (Revenue)'] || 0;
  const revenueA = getRevenue(reportA);
  const revenueB = getRevenue(reportB);

  const percentA = revenueA > 0 ? (valueA / revenueA) * 100 : 0;
  const percentB = revenueB > 0 ? (valueB / revenueB) * 100 : 0;

  // 3. Prepare Labels (using Period string or Branch Name if available)
  const labelA = reportA?.branchName || (reportA?.period ? new Date(reportA.period + '-01').toLocaleString('default', { month: 'short', year: '2-digit' }) : 'A');
  const labelB = reportB?.branchName || (reportB?.period ? new Date(reportB.period + '-01').toLocaleString('default', { month: 'short', year: '2-digit' }) : 'B');
  const labels = [labelA, labelB];

  // 4. Store Data for AI
  if (config.alsoStore) {
    const aiData: any = {};
    aiData[labelA] = `${formatDecimalBasedPercentage(percentA / 100)} (${formatCurrency(valueA)})`;
    aiData[labelB] = `${formatDecimalBasedPercentage(percentB / 100)} (${formatCurrency(valueB)})`;
    
    config.alsoStore(aiData, (v: any) => ({ [`ratio_${config.title.replace(/\s+/g, '_').toLowerCase()}`]: v }));
  }

  // 5. Render Chart (Dual Axis: Bar for Rp, Line for %)
  createChart(config.canvasId, 'bar', {
    labels,
    datasets: [
      {
        type: 'bar',
        label: `${config.title} (Rp)`,
        data: [valueA, valueB],
        backgroundColor: '#3892FF', // Amber-300
        yAxisID: 'y-bar',
        order: 2,
        borderRadius: 4,
      },
      {
        type: 'line',
        label: `${config.title} (%)`,
        data: [percentA, percentB],
        borderColor: '#D97706', // Amber-600
        backgroundColor: '#D97706',
        borderWidth: 2,
        tension: 0.1,
        pointRadius: 4,
        yAxisID: 'y-line',
        order: 1,
      },
    ],
  }, mergeChartOptions(
    {
      scales: {
        'y-bar': {
          type: 'linear',
          position: 'left',
          beginAtZero: true,
          title: { display: true, text: 'Value (Rp)' },
          ticks: { callback: (v: any) => shortenCurrency(v) }
        },
        'y-line': {
          type: 'linear',
          position: 'right',
          beginAtZero: true,
          title: { display: true, text: '% of Revenue' },
          grid: { drawOnChartArea: false },
          ticks: { callback: (v: any) => `${v.toFixed(1)}%` }
        }
      }
    },
    chartTooltip({ 
      label: (ctx: any) => {
        if (ctx.dataset.type === 'line') return `${ctx.dataset.label}: ${ctx.parsed.y.toFixed(1)}%`;
        return `${ctx.dataset.label}: ${formatCurrency(ctx.parsed.y)}`;
      }
    })
  ))
}